"use client";

import { useState, useRef, useCallback, useContext, useEffect, useMemo, ReactNode, memo, forwardRef, KeyboardEvent, lazy, Suspense } from "react";
import type { CSSProperties } from "react";
import { getStagedWindow } from "@/stores/desktopStore";
import { useSandboxSessionStore } from "@/stores/sandboxSessionStore";
import { WindowState, DockAppConfig } from "./types";
import type { WindowType } from "./types";
import { resolveHeroIconSrc } from "./windows/wilsonContentWindows.utils";
import { cn } from "@/lib/utils";
import { getDocumentIconSrc, getDocumentType } from "./windowIcon.utils";
import Image from "next/image";
import { useTheme } from "next-themes";
import { DESKTOP_Z_INDEX } from "./desktopUI.constants";
import { TopBarContext } from "./TopBarContext";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { SWITCHER_APPS, switcherAppsOf, useDockItems } from "./useDockItems";
import { useIsSetupAppVisible } from "./useIsSetupAppVisible";
import { useIsSetupDone } from "./useIsSetupDone";
import { useDesktopShortcuts } from "./useDesktopShortcuts";
import { useWarmWallet } from "./windows/cartagePay/useWarmWallet";
import { useIsPhone } from "@/hooks/useIsPhone";
import { useIsCartageTeamMember } from "@/hooks/useIsCartageTeamMember";
import { useSandboxSession } from "./useSandboxSession";
import { AppBar, AppBarItem, AppBarItemSkeleton, AppBarMoreIcon, AppGrid, Sheet, Glyph, type GlyphName, ContextMenu, ShelfPanel, ShelfRecord, ShelfTile, Pressable, SkeletonText, DockPeek, WilsonFace } from "@/components/desktop/ui";
import { useLocale } from "@/providers/LocaleProvider";
import type { TodayTodo } from "./windows/wilsonHub/scheduledInsights.utils";
import type { NeedsYouGlance } from "./windows/wilsonHub/NeedsYouReader";
const NeedsYouReader = lazy(() => import("./windows/wilsonHub/NeedsYouReader").then(mod => ({
  default: mod.NeedsYouReader
})));
const NO_GLANCE: NeedsYouGlance = {
  needsYouCount: 0,
  todos: []
};
const NAMED_RECORD_TYPES: ReadonlySet<string> = new Set(["shipment", "quote", "salesOrder", "vendor", "addressBook", "product"]);
const MAGNIFICATION_CONFIG = {
  maxScale: 1.2,
  neighborScale: 1.12,
  farScale: 1.05,
  baseScale: 1.0,
  effectRadius: 3
};
const DOCK_NAV_CLASS = "fixed bottom-3 left-1/2 -translate-x-1/2";
const DOCK_PANEL_CLASS = cn("flex items-start gap-0.5 rounded-dock pl-2 pr-[5px] pt-[7px] pb-0.5", "bg-[var(--glass-dock-bg)] backdrop-blur-xl backdrop-saturate-150", "shadow-glass-dock");
const DOCK_ITEM_CLASS = "relative flex w-[50px] flex-col items-center";
const DOCK_ICON_CLASS = "w-9 h-9 rounded-dock-icon flex items-center justify-center overflow-hidden shadow-dock-icon";
const DOCK_LABEL_CLASS = "mt-1 max-w-[62px] truncate whitespace-nowrap text-dock-label font-normal";
const DOCK_DIVIDER_CLASS = "mx-1 mt-[3px] h-[30px] w-px self-start bg-ink/10";
const DOCK_INK_TILE_CLASS = "bg-ink shadow-dock-ink-tile";
const DOCK_INK_TILE_MARK_CLASS = "h-auto w-[22px] text-surface";
const DOCK_ICON_SIZE = 36;
const CHAT_DOCK_APP_ID = "chat";
const sonnerModule = import("sonner");
const showDockToastError = (message: string) => void sonnerModule.then(mod => mod.toast.error(message));
interface DockItemBaseProps {
  label: string;
  isActive: boolean;
  showIndicator: boolean;
  indicatorColor?: "brand" | "muted";
  isMinimized?: boolean;
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  children: ReactNode;
  iconContainerClassName?: string;
  scale?: number;
  itemRef?: React.RefObject<HTMLButtonElement | null>;
  iconOverlay?: ReactNode;
  isInkTile?: boolean;
}
const DockItemBase = memo(function DockItemBase({
  label,
  isActive,
  showIndicator,
  indicatorColor = "brand",
  isMinimized = false,
  onClick,
  ariaLabel,
  children,
  iconContainerClassName,
  scale = 1,
  itemRef,
  iconOverlay,
  isInkTile = false
}: DockItemBaseProps) {
  const indicatorColorClass = {
    brand: isActive ? "bg-ink" : "bg-ink/50",
    muted: isActive ? "bg-ink" : isMinimized ? "bg-ink/30" : "bg-ink/50"
  };
  return <Pressable ref={itemRef} className={cn(DOCK_ITEM_CLASS, "group origin-bottom")} style={{
    transform: `scale(${scale})`,
    transition: "transform 100ms cubic-bezier(0.4, 0, 0.2, 1)"
  }} onClick={e => onClick(e)} aria-label={ariaLabel} aria-pressed={isActive}>
            <div className="relative">
                <div className={cn(DOCK_ICON_CLASS, iconContainerClassName, isInkTile && DOCK_INK_TILE_CLASS)} style={{
        boxShadow: scale > 1.1 && !isInkTile ? "0 4px 12px rgb(var(--ink) / 0.15)" : undefined
      }}>
                    {children}
                </div>
                {iconOverlay}
            </div>
            <span className={cn(DOCK_LABEL_CLASS, isInkTile ? "text-ink" : "text-ink/85")}>
                {label}
            </span>
            <div aria-hidden className={cn("mt-[3px] w-[3px] h-[3px] rounded-full", "transition-opacity duration-fast", indicatorColorClass[indicatorColor], !showIndicator && "opacity-0")} />
        </Pressable>;
});
interface AppIconContentProps {
  iconSrc?: string;
  fallbackIcon?: GlyphName;
  alt: string;
}
function AppIconContent({
  iconSrc,
  fallbackIcon,
  alt
}: AppIconContentProps) {
  if (iconSrc) {
    return <Image src={iconSrc} alt={alt} width={DOCK_ICON_SIZE} height={DOCK_ICON_SIZE} fetchPriority="low" className="w-full h-full object-contain" />;
  }
  if (fallbackIcon) {
    return <Glyph name={fallbackIcon} className="h-5 w-5 text-ink" />;
  }
  return null;
}
function ThemedAppIcon({
  app,
  alt
}: {
  app: DockAppConfig;
  alt: string;
}) {
  const {
    resolvedTheme
  } = useTheme();
  const iconSrc = resolvedTheme === "dark" && app.darkIconSrc ? app.darkIconSrc : app.iconSrc;
  return <AppIconContent iconSrc={iconSrc} fallbackIcon={app.fallbackIcon} alt={alt} data-sentry-element="AppIconContent" data-sentry-component="ThemedAppIcon" data-sentry-source-file="Dock.tsx" />;
}
interface AppDockItemProps {
  app: DockAppConfig;
  isOpen: boolean;
  isActive: boolean;
  onClick: (e?: React.MouseEvent) => void;
  onClose?: () => void;
  scale?: number;
  index: number;
  registerItemRef?: (index: number, ref: HTMLButtonElement | null) => void;
  needsYouCount?: number;
  peek?: React.ReactNode;
}
const AppDockItem = memo(function AppDockItem({
  app,
  isOpen,
  isActive,
  onClick,
  onClose,
  scale,
  index,
  registerItemRef,
  needsYouCount = 0,
  peek
}: AppDockItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tA11y = useClientTranslations("desktop.a11y");
  const tApps = useClientTranslations("desktop.setup.apps");
  const translatedLabel = tApps(app.id);
  const isChat = app.id === CHAT_DOCK_APP_ID;
  useEffect(() => {
    registerItemRef?.(index, buttonRef.current);
    return () => registerItemRef?.(index, null);
  }, [registerItemRef, index]);
  return <div className="relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            {isHovered && peek && <div className="pointer-events-none absolute bottom-full left-1/2 mb-3 -translate-x-1/2">
                    {peek}
                </div>}
            <DockItemBase label={translatedLabel} isActive={isActive} showIndicator={isOpen} indicatorColor="brand" onClick={onClick} ariaLabel={`${translatedLabel}${isOpen ? ` (${tA11y("appOpen")})` : ""}${needsYouCount > 0 ? `, ${tA11y("needsYou", {
      count: needsYouCount
    })}` : ""}`} iconContainerClassName={!app.iconSrc ? "bg-surface/80" : undefined} scale={scale} itemRef={buttonRef} isInkTile={isChat} iconOverlay={isHovered && isOpen && onClose ? <DockCloseBadge label={tA11y("closeApp", {
      label: translatedLabel
    })} onClose={onClose} /> : needsYouCount > 0 ? <DockNeedsYouBadge count={needsYouCount} /> : undefined}>
                {isChat ? <WilsonFace variant="bare" title={translatedLabel} className={DOCK_INK_TILE_MARK_CLASS} /> : <ThemedAppIcon app={app} alt={translatedLabel} />}
            </DockItemBase>
        </div>;
});
const PEEK_ROWS = 5;
function RoutinesDockPeek({
  todos
}: {
  todos: TodayTodo[];
}) {
  const t = useClientTranslations("desktop.dock");
  const {
    locale
  } = useLocale();
  const timeFormat = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit"
  });
  const shown = todos.slice(0, PEEK_ROWS);
  const hiddenCount = todos.length - shown.length;
  return <DockPeek label={t("routinesPeek.label")} emptyLabel={t("routinesPeek.empty")} moreLabel={hiddenCount > 0 ? t("routinesPeek.more", {
    count: hiddenCount
  }) : undefined} rows={shown.map(todo => ({
    key: todo.key,
    time: timeFormat.format(todo.at),
    title: todo.title,
    state: todo.state,
    stateLabel: t(`routinesPeek.states.${todo.state}`)
  }))} data-sentry-element="DockPeek" data-sentry-component="RoutinesDockPeek" data-sentry-source-file="Dock.tsx" />;
}
function DockNeedsYouBadge({
  count
}: {
  count: number;
}) {
  return <span aria-hidden className="pointer-events-none absolute -right-1 -top-1 z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-tone-yellow px-1 font-mono text-caption leading-none tabular-nums text-tag-ink shadow-card" data-sentry-component="DockNeedsYouBadge" data-sentry-source-file="Dock.tsx">
            {count}
        </span>;
}
function DockCloseBadge({
  label,
  onClose
}: {
  label: string;
  onClose: () => void;
}) {
  const close = (event: {
    stopPropagation: () => void;
  }) => {
    event.stopPropagation();
    onClose();
  };
  return <div role="button" tabIndex={0} onClick={close} onKeyDown={event => {
    if (event.key === "Enter" || event.key === " ") close(event);
  }} className={cn("absolute -top-1 -right-1 w-4 h-4 rounded-full z-10", "bg-ink/60 hover:bg-tone-red-ink", "flex items-center justify-center cursor-pointer", "transition-colors duration-fast")} aria-label={label} data-sentry-component="DockCloseBadge" data-sentry-source-file="Dock.tsx">
            <Glyph name="close" className="w-2.5 h-2.5 text-surface" data-sentry-element="Glyph" data-sentry-source-file="Dock.tsx" />
        </div>;
}
export const SANDBOX_DOCK_ICON_SRC = "/desktop/app-sandbox.svg";
function SandboxDockItem({
  index,
  scale,
  registerItemRef
}: {
  index: number;
  scale?: number;
  registerItemRef?: (index: number, ref: HTMLButtonElement | null) => void;
}) {
  const tApps = useClientTranslations("desktop.setup.apps");
  const t = useClientTranslations("desktop.dock");
  const label = tApps("sandbox");
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    registerItemRef?.(index, buttonRef.current);
    return () => registerItemRef?.(index, null);
  }, [registerItemRef, index]);
  const {
    sandboxSessionId,
    isStarting,
    isLeaving,
    start,
    leave
  } = useSandboxSession();
  const [isLeavingLiveRuns, setIsLeavingLiveRuns] = useState(false);
  const handleClick = async () => {
    if (isStarting || isLeaving || isLeavingLiveRuns) return;
    if (sandboxSessionId) {
      leave();
      return;
    }
    setIsLeavingLiveRuns(true);
    try {
      await useSandboxSessionStore.getState().cancelLiveChatRuns();
    } catch {
      setIsLeavingLiveRuns(false);
      showDockToastError(t("sandboxStartBlocked"));
      return;
    }
    setIsLeavingLiveRuns(false);
    start();
  };
  return <DockItemBase label={label} isActive={sandboxSessionId !== null} showIndicator={sandboxSessionId !== null} onClick={handleClick} ariaLabel={label} itemRef={buttonRef} scale={scale} data-sentry-element="DockItemBase" data-sentry-component="SandboxDockItem" data-sentry-source-file="Dock.tsx">
            <AppIconContent iconSrc={SANDBOX_DOCK_ICON_SRC} alt={label} data-sentry-element="AppIconContent" data-sentry-source-file="Dock.tsx" />
        </DockItemBase>;
}
interface DocumentDockItemProps {
  window: WindowState;
  isActive: boolean;
  onClick: (e?: React.MouseEvent) => void;
  onClose: () => void;
  scale?: number;
  index: number;
  registerItemRef?: (index: number, ref: HTMLButtonElement | null) => void;
}
const DocumentDockItem = memo(function DocumentDockItem({
  window: windowState,
  isActive,
  onClick,
  onClose,
  scale,
  index,
  registerItemRef
}: DocumentDockItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isMinimized = windowState.displayState === "minimized";
  const tA11y = useClientTranslations("desktop.a11y");
  useEffect(() => {
    registerItemRef?.(index, buttonRef.current);
    return () => registerItemRef?.(index, null);
  }, [registerItemRef, index]);
  return <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <DockItemBase label={windowState.title} isActive={isActive} showIndicator={true} indicatorColor="muted" isMinimized={isMinimized} onClick={onClick} iconContainerClassName={cn(isMinimized && "opacity-50", isActive ? "bg-cobalt-pale" : "bg-surface/80")} scale={scale} itemRef={buttonRef} iconOverlay={isHovered ? <DockCloseBadge label={tA11y("closeApp", {
      label: windowState.title
    })} onClose={onClose} /> : undefined}>
                <RecordIcon windowState={windowState} isActive={isActive} />
            </DockItemBase>
        </div>;
});
function calculateScale(distance: number, isHovering: boolean): number {
  if (!isHovering) return MAGNIFICATION_CONFIG.baseScale;
  if (distance === 0) return MAGNIFICATION_CONFIG.maxScale;
  if (distance === 1) return MAGNIFICATION_CONFIG.neighborScale;
  if (distance === 2) return MAGNIFICATION_CONFIG.farScale;
  return MAGNIFICATION_CONFIG.baseScale;
}
export function RecordIcon({
  windowState,
  isActive = false
}: {
  windowState: WindowState;
  isActive?: boolean;
}) {
  const {
    resolvedTheme
  } = useTheme();
  const glyphClassName = cn("w-5 h-5", isActive ? "text-cobalt-deep" : "text-ink");
  if (windowState.type === "report") {
    return <Glyph name="chart" className={glyphClassName} />;
  }
  if (windowState.type === "meetWilson") {
    return <Image src={resolveHeroIconSrc("/setup/icons/document-architecture.svg", resolvedTheme === "dark")} alt="" width={DOCK_ICON_SIZE} height={DOCK_ICON_SIZE} className="w-full h-full object-contain" />;
  }
  const docIconSrc = getDocumentIconSrc(windowState.data);
  if (docIconSrc) {
    return <Image src={docIconSrc} alt="" width={24} height={24} className="object-contain" />;
  }
  return <Glyph name="fileText" className={glyphClassName} data-sentry-element="Glyph" data-sentry-component="RecordIcon" data-sentry-source-file="Dock.tsx" />;
}
function useRecordKind(): (windowState: WindowState) => string {
  const tApps = useClientTranslations("desktop.setup.apps");
  const tDetail = useClientTranslations("desktop.documentDetail");
  return (windowState: WindowState) => {
    const documentType = getDocumentType(windowState.data);
    return documentType && NAMED_RECORD_TYPES.has(documentType) ? tDetail(`tabLabels.${documentType}`) : tApps(windowState.type);
  };
}
function appIconOf(app: DockAppConfig) {
  return <ThemedAppIcon app={app} alt="" data-sentry-element="ThemedAppIcon" data-sentry-component="appIconOf" data-sentry-source-file="Dock.tsx" />;
}
function useWarmApp() {
  const warmWallet = useWarmWallet();
  return useCallback(({
    app,
    isOpen
  }: {
    app: DockAppConfig;
    isOpen: boolean;
  }) => {
    if (app.type === "cartagePay" && !isOpen) warmWallet();
  }, [warmWallet]);
}
function AppTiles({
  shouldMarkCurrent,
  onDone
}: {
  shouldMarkCurrent: boolean;
  onDone: () => void;
}) {
  const {
    activeWindowId,
    apps,
    windowsByType,
    selectApp,
    isAppOpen
  } = useDockItems();
  const tApps = useClientTranslations("desktop.setup.apps");
  const warmApp = useWarmApp();
  return apps.map(app => {
    const appWindow = windowsByType[app.type];
    return <ShelfTile key={app.id} icon={appIconOf(app)} label={tApps(app.id)} onPointerEnter={() => warmApp({
      app,
      isOpen: isAppOpen(app)
    })} isOpen={isAppOpen(app)} isCurrent={shouldMarkCurrent && !!appWindow && activeWindowId === appWindow.id} onSelect={() => {
      selectApp({
        app,
        source: "shelf",
        shouldSendBackStaged: false
      });
      onDone();
    }} />;
  });
}
function OpenRecords({
  onDone
}: {
  onDone: () => void;
}) {
  const {
    activeWindowId,
    recordWindows,
    selectRecord,
    closeWindow
  } = useDockItems();
  const tA11y = useClientTranslations("desktop.a11y");
  const recordKind = useRecordKind();
  return recordWindows.map(windowState => <ShelfRecord key={windowState.id} icon={<RecordIcon windowState={windowState} />} title={windowState.title} kind={recordKind(windowState)} isCurrent={activeWindowId === windowState.id} closeLabel={tA11y("closeApp", {
    label: windowState.title
  })} onSelect={() => {
    selectRecord({
      windowState,
      source: "shelf",
      shouldSendBackStaged: false
    });
    onDone();
  }} onClose={() => closeWindow(windowState.id)} />);
}
const PHONE_BAR_APP_TYPES: readonly WindowType[] = ["explorer", "comms", "todos", "reports", "freightDashboard"];
const PHONE_BAR_APPS = PHONE_BAR_APP_TYPES.flatMap(type => SWITCHER_APPS.find(app => app.type === type) ?? []);
const SETUP_APP = SWITCHER_APPS.find(app => app.type === "setup");
export function phoneBarAppsOf({
  isSetupDone,
  isSetupAppVisible
}: {
  isSetupDone: boolean;
  isSetupAppVisible: boolean;
}): DockAppConfig[] {
  if (isSetupDone || !isSetupAppVisible || !SETUP_APP) return PHONE_BAR_APPS;
  return [SETUP_APP, ...PHONE_BAR_APPS.slice(0, -1)];
}
export function Dock() {
  const isPhone = useIsPhone();
  return isPhone ? <PhoneDock data-sentry-element="PhoneDock" data-sentry-component="Dock" data-sentry-source-file="Dock.tsx" /> : <DesktopDock data-sentry-element="DesktopDock" data-sentry-component="Dock" data-sentry-source-file="Dock.tsx" />;
}
function PhoneDock() {
  const {
    windows,
    recordWindows,
    selectApp
  } = useDockItems();
  const t = useClientTranslations("desktop.dock");
  const tA11y = useClientTranslations("desktop.a11y");
  const tApps = useClientTranslations("desktop.setup.apps");
  const tShelf = useClientTranslations("desktop.shelf");
  const {
    resolvedTheme
  } = useTheme();
  const shortcuts = useDesktopShortcuts();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const isPageStaged = !!getStagedWindow(windows);
  const isSetupDone = useIsSetupDone();
  const isSetupAppVisible = useIsSetupAppVisible();
  const phoneBarApps = phoneBarAppsOf({
    isSetupDone,
    isSetupAppVisible
  });
  const openApp = (app: DockAppConfig) => {
    setIsSheetOpen(false);
    selectApp({
      app,
      source: "phoneBar",
      shouldSendBackStaged: false
    });
  };
  return <>
            <AppBar label={tA11y("applicationDock")} isHidden={isPageStaged} style={{
      zIndex: DESKTOP_Z_INDEX.DOCK
    }} data-sentry-element="AppBar" data-sentry-source-file="Dock.tsx">
                {phoneBarApps.map(app => <AppBarItem key={app.id} icon={appIconOf(app)} label={tApps(app.id)} onSelect={() => openApp(app)} />)}
                <AppBarItem icon={<AppBarMoreIcon />} label={t("allApps")} aria-haspopup="dialog" aria-expanded={isSheetOpen} onSelect={() => setIsSheetOpen(true)} data-sentry-element="AppBarItem" data-sentry-source-file="Dock.tsx" />
            </AppBar>
            <Sheet side="bottom" isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)} title={t("apps")} data-sentry-element="Sheet" data-sentry-source-file="Dock.tsx">
                <AppGrid data-sentry-element="AppGrid" data-sentry-source-file="Dock.tsx">
                    <AppTiles shouldMarkCurrent={false} onDone={() => setIsSheetOpen(false)} data-sentry-element="AppTiles" data-sentry-source-file="Dock.tsx" />
                    {shortcuts.map(shortcut => <ShelfTile key={shortcut.id} icon={<Image src={resolveHeroIconSrc(shortcut.iconSrc, resolvedTheme === "dark")} alt="" width={44} height={44} className="h-full w-full object-contain" />} label={shortcut.label} onSelect={() => {
          setIsSheetOpen(false);
          shortcut.onOpen();
        }} />)}
                </AppGrid>
                {recordWindows.length > 0 && <div className="flex flex-col gap-1 px-3 pt-5">
                        <span className="px-1 text-xs text-ink-3">
                            {tShelf("open")}
                        </span>
                        <OpenRecords onDone={() => setIsSheetOpen(false)} />
                    </div>}
            </Sheet>
        </>;
}
function DesktopDock() {
  const {
    windows,
    activeWindowId,
    apps,
    windowsByType,
    recordWindows,
    selectApp,
    selectRecord,
    closeWindow,
    isAppOpen
  } = useDockItems();
  const t = useClientTranslations("desktop.dock");
  const tA11y = useClientTranslations("desktop.a11y");
  const [{
    needsYouCount,
    todos: routinesToday
  }, setGlance] = useState<NeedsYouGlance>(NO_GLANCE);
  const tApps = useClientTranslations("desktop.setup.apps");
  const isPageStaged = !!getStagedWindow(windows);
  const isOverSearch = useContext(TopBarContext)?.isSpotlightSearching ?? false;
  const warmApp = useWarmApp();
  const isCartageTeamMember = useIsCartageTeamMember();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const handleDockMouseLeave = useCallback(() => setHoveredIndex(null), []);
  const handleHover = useCallback((index: number) => setHoveredIndex(index), []);
  const registerItemRef = useCallback((index: number, ref: HTMLButtonElement | null) => {
    if (ref) {
      itemRefs.current.set(index, ref);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    const allIndices = Array.from(itemRefs.current.keys()).sort((a, b) => a - b);
    if (allIndices.length === 0) return;
    const currentIndex = allIndices.indexOf(focusedIndex);
    const focusAt = (index: number) => {
      setFocusedIndex(index);
      itemRefs.current.get(index)?.focus();
    };
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      focusAt(currentIndex < allIndices.length - 1 ? allIndices[currentIndex + 1] : allIndices[0]);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      focusAt(currentIndex > 0 ? allIndices[currentIndex - 1] : allIndices[allIndices.length - 1]);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusAt(allIndices[0]);
    } else if (e.key === "End") {
      e.preventDefault();
      focusAt(allIndices[allIndices.length - 1]);
    }
  }, [focusedIndex]);
  const handleItemContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setHoveredIndex(null);
  }, []);
  const chatApp = apps.find(app => app.id === CHAT_DOCK_APP_ID);
  const middleApps = apps.filter(app => app.id !== CHAT_DOCK_APP_ID);
  const middleStartIndex = chatApp ? 1 : 0;
  const recordStartIndex = apps.length;
  const sandboxIndex = apps.length + recordWindows.length;
  const dockScales = useMemo(() => {
    const total = apps.length + recordWindows.length + (isCartageTeamMember ? 1 : 0);
    return Array.from({
      length: total
    }, (_, i) => calculateScale(hoveredIndex !== null ? Math.abs(hoveredIndex - i) : Infinity, hoveredIndex !== null));
  }, [hoveredIndex, apps.length, recordWindows.length, isCartageTeamMember]);
  const renderApp = (app: DockAppConfig, i: number) => {
    const appWindow = windowsByType[app.type];
    const closeAction = appWindow ? () => closeWindow(appWindow.id) : undefined;
    const contextMenuGroups = closeAction ? [[{
      label: t("closeWindow"),
      onSelect: closeAction
    }]] : [];
    return <ContextMenu key={app.id} groups={contextMenuGroups} label={tApps(app.id)} data-sentry-element="ContextMenu" data-sentry-component="renderApp" data-sentry-source-file="Dock.tsx">
                <div onMouseEnter={() => {
        handleHover(i);
        warmApp({
          app,
          isOpen: isAppOpen(app)
        });
      }} onContextMenu={handleItemContextMenu}>
                    <AppDockItem app={app} isOpen={isAppOpen(app)} isActive={activeWindowId === appWindow?.id} onClick={(e?: React.MouseEvent) => selectApp({
          app,
          source: "dock",
          isModifierClick: !!e?.metaKey || !!e?.ctrlKey
        })} onClose={closeAction} scale={dockScales[i]} index={i} registerItemRef={registerItemRef} needsYouCount={app.type === "todos" ? needsYouCount : 0} peek={app.type === "todos" ? <RoutinesDockPeek todos={routinesToday} /> : undefined} data-sentry-element="AppDockItem" data-sentry-source-file="Dock.tsx" />
                </div>
            </ContextMenu>;
  };
  return <nav className={cn(DOCK_NAV_CLASS, "transition-[transform,opacity] duration-slow ease-out motion-reduce:transition-opacity", isPageStaged && "pointer-events-none translate-y-[calc(100%+24px)] opacity-0 motion-reduce:translate-y-0", isOverSearch && !isPageStaged && "pointer-events-auto")} style={{
    zIndex: DESKTOP_Z_INDEX.DOCK
  }} aria-label={tA11y("applicationDock")} data-desktop-dock aria-hidden={isPageStaged || undefined} inert={isPageStaged} data-sentry-component="DesktopDock" data-sentry-source-file="Dock.tsx">
            <Suspense fallback={null} data-sentry-element="Suspense" data-sentry-source-file="Dock.tsx">
                <NeedsYouReader onChange={setGlance} data-sentry-element="NeedsYouReader" data-sentry-source-file="Dock.tsx" />
            </Suspense>
            <div className={DOCK_PANEL_CLASS} role="toolbar" aria-label={tA11y("openApplications")} onMouseLeave={handleDockMouseLeave} onKeyDown={handleKeyDown}>
                {chatApp && <>
                        {renderApp(chatApp, 0)}
                        <div className={DOCK_DIVIDER_CLASS} />
                    </>}
                {middleApps.map((app, i) => renderApp(app, middleStartIndex + i))}

                {recordWindows.length > 0 && <div className={DOCK_DIVIDER_CLASS} />}

                {recordWindows.map((windowState, i) => {
        const currentIndex = recordStartIndex + i;
        return <div key={windowState.id} onMouseEnter={() => handleHover(currentIndex)}>
                            <DocumentDockItem window={windowState} isActive={activeWindowId === windowState.id} onClick={() => selectRecord({
            windowState,
            source: "dock"
          })} onClose={() => closeWindow(windowState.id)} scale={dockScales[currentIndex]} index={currentIndex} registerItemRef={registerItemRef} />
                        </div>;
      })}

                {isCartageTeamMember && <>
                        <div className={DOCK_DIVIDER_CLASS} />
                        <div onMouseEnter={() => handleHover(sandboxIndex)}>
                            <SandboxDockItem index={sandboxIndex} scale={dockScales[sandboxIndex]} registerItemRef={registerItemRef} />
                        </div>
                    </>}
            </div>
        </nav>;
}
export function DockSkeleton() {
  const tA11y = useClientTranslations("desktop.a11y");
  const tApps = useClientTranslations("desktop.setup.apps");
  const t = useClientTranslations("desktop.dock");
  const isCartageTeamMember = useIsCartageTeamMember();
  return <>
            <nav className={cn(DOCK_NAV_CLASS, "hidden md:block")} style={{
      zIndex: DESKTOP_Z_INDEX.DOCK
    }}>
                <div className={DOCK_PANEL_CLASS}>
                    <div className={DOCK_ITEM_CLASS}>
                        <div className={cn(DOCK_ICON_CLASS, DOCK_INK_TILE_CLASS)}>
                            <WilsonFace variant="bare" className={DOCK_INK_TILE_MARK_CLASS} data-sentry-element="WilsonFace" data-sentry-source-file="Dock.tsx" />
                        </div>
                        <SkeletonText className={DOCK_LABEL_CLASS} data-sentry-element="SkeletonText" data-sentry-source-file="Dock.tsx">
                            {tApps(CHAT_DOCK_APP_ID)}
                        </SkeletonText>
                    </div>
                    <div className={DOCK_DIVIDER_CLASS} />
                    {switcherAppsOf({
          isSetupAppVisible: false
        }).filter(app => app.id !== CHAT_DOCK_APP_ID).map(app => <div key={app.id} className={DOCK_ITEM_CLASS}>
                                <div className={DOCK_ICON_CLASS}>
                                    {appIconOf(app)}
                                </div>
                                <SkeletonText className={DOCK_LABEL_CLASS}>
                                    {tApps(app.id)}
                                </SkeletonText>
                            </div>)}

                    {isCartageTeamMember && <>
                            <div className={DOCK_DIVIDER_CLASS} />
                            <div className={DOCK_ITEM_CLASS}>
                                <div className={DOCK_ICON_CLASS}>
                                    <AppIconContent iconSrc={SANDBOX_DOCK_ICON_SRC} alt={tApps("sandbox")} />
                                </div>
                                <SkeletonText className={DOCK_LABEL_CLASS}>
                                    {tApps("sandbox")}
                                </SkeletonText>
                            </div>
                        </>}
                </div>
            </nav>
            <AppBar label={tA11y("applicationDock")} className="md:hidden" style={{
      zIndex: DESKTOP_Z_INDEX.DOCK
    }} data-sentry-element="AppBar" data-sentry-source-file="Dock.tsx">
                {PHONE_BAR_APPS.map(app => <AppBarItemSkeleton key={app.id} icon={appIconOf(app)} sizeLabel={tApps(app.id)} />)}
                <AppBarItemSkeleton icon={<AppBarMoreIcon />} sizeLabel={t("allApps")} data-sentry-element="AppBarItemSkeleton" data-sentry-source-file="Dock.tsx" />
            </AppBar>
        </>;
}
export const DockShelf = forwardRef<HTMLDivElement, {
  id?: string;
  style?: CSSProperties;
  onDone: () => void;
}>(function DockShelf({
  id,
  style,
  onDone
}, ref) {
  const {
    recordWindows
  } = useDockItems();
  const tShelf = useClientTranslations("desktop.shelf");
  return <ShelfPanel ref={ref} id={id} aria-label={tShelf("label")} style={style} openLabel={tShelf("open")} records={recordWindows.length > 0 ? <OpenRecords onDone={onDone} /> : undefined} apps={<AppTiles shouldMarkCurrent onDone={onDone} />} />;
});